/**
 * Client des Graph-Explorers (`/administration/graph/*` auf tri-server, nur Plattform-Admins).
 *
 * Wie `SynonymsAPI`: `authenticatedFetch` + `processResponse`. Die Formen hier spiegeln die des
 * Servers — sie werden nicht umgerechnet, denn der Explorer soll zeigen, was der Server liefert,
 * und nicht, was der Client daraus macht.
 */
import { configuration } from "./Configuration";
import { ApplicationError, UnauthorizedError } from "./general/Errors";
import { authenticatedFetch } from "./JWTAuthService";

export interface GraphTypeSummary {
  name: string;
  label: string;
  abstract: boolean;
  public: boolean;
  subtypes: string[];
  fieldCount: number;
  relationCount: number;
}

export interface GraphFieldDescription {
  name: string;
  label: string | null;
  type: string;
  role: string;
  enum?: string[];
  filled: number | null;
}

export interface GraphTypeDescription {
  type: string;
  label: string | null;
  public: boolean;
  abstract: boolean;
  subtypes: string[];
  fields: GraphFieldDescription[];
  relations: Array<{
    name: string;
    label: string | null;
    target: string;
    cardinality: string;
    polymorphic?: string[];
    inverse?: string | null;
  }>;
  keys: Array<{ name: string; fields: string[]; sharpness: string }>;
}

export interface GraphNode {
  id: string;
  type: string;
  label: string;
  attributes: Record<string, any>;
}

export interface GraphEdge {
  id: string;
  source: string;
  target: string;
  relation: string;
  label: string;
  direction: "out" | "in";
}

export interface GraphSearchResult {
  nodes: GraphNode[];
  total: number;
  capped: boolean;
  /** Was der Parser nicht verstanden hat — angezeigt, nie verschluckt. */
  problems: string[];
  filter: Record<string, any>;
}

export interface GraphJourney {
  source: GraphNode;
  nodes: GraphNode[];
  edges: GraphEdge[];
  /** Knoten-Id → Ebene (Entfernung vom Ausgangsobjekt). */
  levels: Record<string, number>;
  depth: number;
  /** Was nicht mitgenommen wurde — angezeigt, nie verschwiegen. */
  truncated: string | null;
}

export interface GraphExpansion {
  source: GraphNode;
  nodes: GraphNode[];
  edges: GraphEdge[];
  truncated: Array<{ relation: string; direction: string; shown: number; total: number }>;
}

const processResponse = async (response: Response) => {
  if (response.ok) return response.json();
  if (response.status === 401) throw new UnauthorizedError();
  let errorMessage = `${response.status} ${response.statusText}`;
  try {
    const body = await response.json();
    errorMessage = body.message || body.error || errorMessage;
  } catch {
    // kein JSON-Körper
  }
  throw new ApplicationError(errorMessage);
};

export const useGraphExplorerAPI = () => {
  const baseUrl = `${configuration.serverUrl}/administration/graph`;

  const get = async (path: string) =>
    processResponse(await authenticatedFetch(`${baseUrl}${path}`, { method: "GET" }));

  const post = async (path: string, body: any) =>
    processResponse(
      await authenticatedFetch(`${baseUrl}${path}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    );

  return {
    listTypes: (): Promise<{ types: GraphTypeSummary[] }> => get("/types"),
    describeType: (type: string): Promise<GraphTypeDescription> => get(`/types/${encodeURIComponent(type)}`),
    search: (type: string, query: string, limit = 50): Promise<GraphSearchResult> =>
      post("/search", { type, query, limit }),
    getNode: (type: string, id: string): Promise<{ node: GraphNode; type: GraphTypeDescription }> =>
      get(`/node/${encodeURIComponent(type)}/${encodeURIComponent(id)}`),
    expand: (type: string, id: string, relations?: string[]): Promise<GraphExpansion> =>
      post("/expand", { type, id, ...(relations?.length ? { relations } : {}) }),
    journey: (type: string, id: string, maxDepth = 10): Promise<GraphJourney> =>
      post("/journey", { type, id, maxDepth }),
  };
};
